import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import type { DailyMode, Difficulty, RepsInReserve, SessionKind, SessionTemplate, Settings, TrainingProgram, WorkoutExerciseResult, WorkoutRecord } from '../types'
import { saveWorkout } from '../storage/db'
import { localDateKey } from '../utils/date'
import { RangeInput } from '../components/RangeInput'
import { Timer } from '../components/Timer'
import { NumberWheel } from '../components/NumberWheel'
import { ExerciseGuide } from '../components/ExerciseGuide'
import { ExerciseMedia } from '../components/ExerciseMedia'
import { claimCommit, createCommitGate, releaseCommit } from './commitGate'
import { formatRepetitions, formatSeconds } from '../utils/format'

interface WorkoutFlowProps {
  program: TrainingProgram
  day: number
  session: SessionTemplate
  originalSession: SessionKind
  dailyMode: Exclude<DailyMode, 'rest'>
  recommendationReason?: string
  initialEnergy?: number
  initialKnee?: number
  settings?: Settings
  onClose: () => void
  onSaved: () => void
}

type Phase = 'checkin' | 'exercise' | 'rest' | 'finish' | 'saved'
interface FlowState { phase: Phase; exerciseIndex: number; setIndex: number; motion: 'none' | 'forward' | 'back'; transitionId: number; restSeconds?: number }
type FlowAction =
  | { type: 'BEGIN' }
  | { type: 'COMPLETE'; setCount: number; exerciseCount: number; hasRest: boolean; restSeconds?: number; circuit: boolean; roundCount: number; circuitStart: number; circuitEnd: number }
  | { type: 'REST_DONE' }
  | { type: 'SKIP'; exerciseCount: number }
  | { type: 'RESTORE'; state: FlowState }
  | { type: 'SAVED' }

function flowReducer(state: FlowState, action: FlowAction): FlowState {
  if (action.type === 'BEGIN') return { ...state, phase: 'exercise', motion: 'forward', transitionId: state.transitionId + 1 }
  if (action.type === 'REST_DONE') return { ...state, phase: 'exercise', restSeconds: undefined, motion: 'forward', transitionId: state.transitionId + 1 }
  if (action.type === 'SAVED') return { ...state, phase: 'saved', motion: 'forward', transitionId: state.transitionId + 1 }
  if (action.type === 'RESTORE') return { ...action.state, motion: 'back', transitionId: state.transitionId + 1 }
  if (action.type === 'SKIP') {
    const finished = state.exerciseIndex >= action.exerciseCount - 1
    return finished
      ? { ...state, phase: 'finish', motion: 'forward', transitionId: state.transitionId + 1 }
      : { phase: 'exercise', exerciseIndex: state.exerciseIndex + 1, setIndex: 0, motion: 'forward', transitionId: state.transitionId + 1 }
  }
  if (action.circuit) {
    const lastExercise = state.exerciseIndex >= action.circuitEnd
    const lastRound = state.setIndex >= action.roundCount - 1
    if (lastExercise && lastRound) {
      const finished = action.circuitEnd >= action.exerciseCount - 1
      return finished
        ? { ...state, phase: 'finish', motion: 'forward', transitionId: state.transitionId + 1 }
        : { phase: 'exercise', exerciseIndex: action.circuitEnd + 1, setIndex: 0, motion: 'forward', transitionId: state.transitionId + 1 }
    }
    if (lastExercise) return { phase: action.hasRest ? 'rest' : 'exercise', exerciseIndex: action.circuitStart, setIndex: state.setIndex + 1, restSeconds: action.restSeconds, motion: 'forward', transitionId: state.transitionId + 1 }
    return { ...state, exerciseIndex: state.exerciseIndex + 1, phase: 'exercise', motion: 'forward', transitionId: state.transitionId + 1 }
  }
  const lastSet = state.setIndex >= action.setCount - 1
  if (!lastSet) return { ...state, setIndex: state.setIndex + 1, phase: action.hasRest ? 'rest' : 'exercise', restSeconds: action.restSeconds, motion: 'forward', transitionId: state.transitionId + 1 }
  const lastExercise = state.exerciseIndex >= action.exerciseCount - 1
  if (lastExercise) return { ...state, phase: 'finish', motion: 'forward', transitionId: state.transitionId + 1 }
  return { phase: 'exercise', exerciseIndex: state.exerciseIndex + 1, setIndex: 0, motion: 'forward', transitionId: state.transitionId + 1 }
}

interface UndoState {
  flow: FlowState
  key: string
  ratings: Record<string, Difficulty>
  rirs: Record<string, RepsInReserve>
  selectedRir: RepsInReserve
  message: string
}

const rirDifficulty: Record<RepsInReserve, Difficulty> = { 0: 5, 1: 4, 2: 3, 3: 2, 4: 1 }
const sideSetLabels: Record<string, [string, string]> = {
  ankle_mobility: ['Левая стопа', 'Правая стопа'],
  open_book: ['Лёжа на левом боку', 'Лёжа на правом боку'],
  bird_dog: ['Левая рука + правая нога', 'Правая рука + левая нога'],
  thoracic_rotation: ['Поворот влево', 'Поворот вправо'],
  side_plank: ['Левый бок', 'Правый бок'],
  overhead_arm_pull: ['Левая рука', 'Правая рука'],
  thigh_stretch: ['Левая нога', 'Правая нога'],
  single_leg_over: ['Левая нога', 'Правая нога']
}

export function WorkoutFlow({ program, day, session, originalSession, dailyMode, recommendationReason, initialEnergy = 6, initialKnee = 0, settings, onClose, onSaved }: WorkoutFlowProps) {
  const needsCheckin = session.kind === 'A' || session.kind === 'B' || session.kind === 'C' || session.kind === 'CHECK'
  const [flow, dispatch] = useReducer(flowReducer, { phase: needsCheckin ? 'checkin' : 'exercise', exerciseIndex: 0, setIndex: 0, motion: 'none', transitionId: 0 })
  const [energyBefore, setEnergyBefore] = useState(initialEnergy)
  const [energyAfter, setEnergyAfter] = useState(Math.min(10, initialEnergy + 1))
  const [kneeBefore, setKneeBefore] = useState(initialKnee)
  const [kneeDuring, setKneeDuring] = useState<number | undefined>()
  const [kneeAfter, setKneeAfter] = useState(initialKnee)
  const [rir, setRir] = useState<RepsInReserve>(2)
  const [overallDifficulty, setOverallDifficulty] = useState<Difficulty>(3)
  const [notes, setNotes] = useState('')
  const [startedAt] = useState(new Date())
  const [actual, setActual] = useState<Record<string, number[]>>(() => Object.fromEntries(session.exercises.map((item) => [item.exerciseId, [...item.sets]])))
  const [ratings, setRatings] = useState<Record<string, Difficulty>>({})
  const [rirs, setRirs] = useState<Record<string, RepsInReserve>>({})
  const [skipped, setSkipped] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [undoState, setUndoState] = useState<UndoState>()
  const [showPainChoice, setShowPainChoice] = useState(false)
  const commitGate = useRef(createCommitGate())
  const undoTimer = useRef<number | undefined>(undefined)
  const saveGuard = useRef(false)

  const library = useMemo(() => new Map(program.exerciseLibrary.map((item) => [item.id, item])), [program.exerciseLibrary])
  const planned = session.exercises[flow.exerciseIndex]
  const exercise = planned ? library.get(planned.exerciseId) : undefined
  const sideLabel = planned?.sets.length === 2 && exercise ? sideSetLabels[exercise.id]?.[flow.setIndex] : undefined
  const circuitStart = session.exercises.findIndex((item) => item.section === 'work')
  const circuitEnd = session.exercises.reduce((last, item, index) => item.section === 'work' ? index : last, -1)

  useEffect(() => () => { if (undoTimer.current) window.clearTimeout(undoTimer.current) }, [])

  const clearUndoLater = () => {
    if (undoTimer.current) window.clearTimeout(undoTimer.current)
    undoTimer.current = window.setTimeout(() => setUndoState(undefined), 5000)
  }

  const completeSet = useCallback(() => {
    if (!planned || !exercise) return
    const key = `${flow.exerciseIndex}:${flow.setIndex}`
    if (!claimCommit(commitGate.current, key, performance.now())) return
    if (settings?.haptics && 'vibrate' in navigator) navigator.vibrate(8)

    const nextRatings = exercise.category === 'strength' ? { ...ratings, [exercise.id]: rirDifficulty[rir] } : ratings
    const nextRirs = exercise.category === 'strength' ? { ...rirs, [exercise.id]: rir } : rirs
    setUndoState({
      flow,
      key,
      ratings,
      rirs,
      selectedRir: rir,
      message: `${exercise.name}: ${planned.unit === 'seconds' ? formatSeconds(actual[planned.exerciseId]?.[flow.setIndex] ?? planned.sets[flow.setIndex]) : formatRepetitions(actual[planned.exerciseId]?.[flow.setIndex] ?? planned.sets[flow.setIndex])}`
    })
    setRatings(nextRatings)
    setRirs(nextRirs)
    dispatch({ type: 'COMPLETE', setCount: planned.sets.length, exerciseCount: session.exercises.length, hasRest: Boolean(planned.restSeconds), restSeconds: planned.restSeconds, circuit: session.format === 'circuit' && planned.section === 'work', roundCount: session.rounds ?? planned.sets.length, circuitStart, circuitEnd })
    if (flow.setIndex >= planned.sets.length - 1) setRir(2)
    clearUndoLater()
  }, [actual, circuitEnd, circuitStart, exercise, flow, planned, ratings, rir, rirs, session.exercises.length, session.format, session.rounds, settings?.haptics])

  if (!exercise && flow.phase !== 'finish' && flow.phase !== 'saved' && flow.phase !== 'checkin') return null

  const updateReps = (value: number) => {
    if (!planned) return
    setActual((current) => {
      const next = [...(current[planned.exerciseId] || planned.sets)]
      next[flow.setIndex] = Math.max(0, value)
      return { ...current, [planned.exerciseId]: next }
    })
  }

  const undo = () => {
    if (!undoState) return
    releaseCommit(commitGate.current, undoState.key)
    setRatings(undoState.ratings)
    setRirs(undoState.rirs)
    setRir(undoState.selectedRir)
    dispatch({ type: 'RESTORE', state: undoState.flow })
    setUndoState(undefined)
    if (undoTimer.current) window.clearTimeout(undoTimer.current)
  }

  const skipExercise = () => {
    if (!exercise || !planned) return
    setSkipped((current) => new Set(current).add(exercise.id))
    setActual((current) => ({ ...current, [exercise.id]: planned.sets.map(() => 0) }))
    setShowPainChoice(false)
    dispatch({ type: 'SKIP', exerciseCount: session.exercises.length })
  }

  const reportKneePain = () => {
    setKneeDuring(Math.max(kneeDuring || 0, kneeBefore || 1))
    setShowPainChoice(true)
  }

  const finish = async () => {
    if (saveGuard.current) return
    saveGuard.current = true
    setSaving(true)
    setError('')
    try {
      const completedAt = new Date()
      const id = crypto.randomUUID()
      const workout: WorkoutRecord = {
        id,
        date: localDateKey(),
        day,
        programId: program.id,
        programVersion: program.version,
        sessionType: session.kind,
        originalSession,
        actualSession: session.kind,
        dailyMode,
        recommendationReason,
        modified: originalSession !== session.kind || dailyMode !== 'standard' || session.kind === 'MIN',
        minimal: dailyMode === 'light' || session.kind === 'MIN',
        skipped: false,
        durationMin: Math.max(1, Math.round((completedAt.getTime() - startedAt.getTime()) / 60_000)),
        startedAt: startedAt.toISOString(),
        completedAt: completedAt.toISOString(),
        energyBefore,
        energyAfter,
        overallDifficulty,
        kneeBefore: needsCheckin ? kneeBefore : undefined,
        kneeDuring,
        kneeAfter: needsCheckin ? kneeAfter : undefined,
        notes
      }
      const results: WorkoutExerciseResult[] = session.exercises.map((item) => ({
        id: `${id}:${item.exerciseId}`,
        workoutId: id,
        exerciseId: item.exerciseId,
        plannedSets: item.sets,
        actualSets: actual[item.exerciseId] || item.sets,
        unit: item.unit,
        difficulty: ratings[item.exerciseId],
        rir: rirs[item.exerciseId],
        pain: library.get(item.exerciseId)?.kneeSensitive ? (kneeDuring || 0) : 0,
        skipped: skipped.has(item.exerciseId),
        notes: ''
      }))
      await saveWorkout(workout, results)
      dispatch({ type: 'SAVED' })
      onSaved()
    } catch (caught) {
      console.error(caught)
      saveGuard.current = false
      setError('Не удалось сохранить тренировку. Данные на экране сохранены — попробуй ещё раз.')
    } finally { setSaving(false) }
  }

  const motionClass = flow.motion === 'none' ? '' : ` flow-enter flow-enter--${flow.motion}`

  return (
    <div className="workout-overlay" role="dialog" aria-modal="true" aria-label="Тренировка">
      <header className="workout-header">
        <button className="icon-button" aria-label="Закрыть тренировку" onClick={() => { if (window.confirm('Закрыть тренировку? Несохранённые данные будут потеряны.')) onClose() }}>×</button>
        {flow.phase === 'exercise' && <span className="tabular">{session.format === 'circuit' && planned?.section === 'work' ? `Круг ${flow.setIndex + 1}/${session.rounds}` : `${flow.exerciseIndex + 1} / ${session.exercises.length} · ${sideLabel ? `сторона ${flow.setIndex + 1}/2` : `подход ${flow.setIndex + 1}/${planned?.sets.length}`}`}</span>}
        <span className="workout-header__kind">{session.name}</span>
      </header>

      <div className="sr-only" aria-live="polite">{flow.phase === 'exercise' && exercise ? `${exercise.name}, подход ${flow.setIndex + 1}` : flow.phase === 'rest' ? 'Отдых' : ''}</div>

      {flow.phase === 'checkin' && <section key={`checkin-${flow.transitionId}`} className={`workout-panel workout-panel--center${motionClass}`}>
        <p className="eyebrow">Перед началом</p>
        <h1>Проверим самочувствие</h1>
        <p className="muted">Ответы сохранятся в истории и помогут выбрать нагрузку в следующий раз.</p>
        <RangeInput label="Энергия" value={energyBefore} onChange={setEnergyBefore} leftLabel="нет сил" rightLabel="много сил" />
        <RangeInput label="Правое колено" value={kneeBefore} onChange={setKneeBefore} leftLabel="ничего" rightLabel="сильная боль" />
        {kneeBefore >= 5 && <div className="notice notice--warning">Не делай движения через боль. Лучше закрыть тренировку и выбрать отдых или лёгкий режим.</div>}
        <button className="button button--primary button--wide" onClick={() => dispatch({ type: 'BEGIN' })}>Продолжить</button>
      </section>}

      {flow.phase === 'exercise' && exercise && planned && <section key={`${exercise.id}-${flow.setIndex}-${flow.transitionId}`} className={`workout-panel exercise-step${motionClass}`}>
        <div className="exercise-stage">
          <span className="exercise-stage__count">{String(flow.exerciseIndex + 1).padStart(2, '0')}<small>/{String(session.exercises.length).padStart(2, '0')}</small></span>
          <ExerciseMedia exercise={exercise} />
        </div>

        <div className="exercise-command">
          <div className="exercise-command__meta"><span>{session.format === 'circuit' && planned.section === 'work' ? `Круг ${flow.setIndex + 1} из ${session.rounds}` : sideLabel || (planned.sets.length > 1 ? `Подход ${flow.setIndex + 1}/${planned.sets.length}` : exercise.category === 'warmup' ? 'Разминка' : 'Сейчас')}</span><span>{planned.unit === 'reps' ? planned.label || `План ${planned.sets[flow.setIndex]}` : planned.label || `${planned.sets[flow.setIndex]} сек`}</span></div>
          <h1>{exercise.name}</h1>
          {exercise.aliases?.length ? <p className="exercise-alias">{exercise.aliases.join(' · ')}</p> : null}
          <p className="exercise-cue">{exercise.shortCue}</p>
        </div>

        {planned.unit === 'seconds' ? (
          <Timer seconds={actual[planned.exerciseId]?.[flow.setIndex] ?? planned.sets[flow.setIndex]} title={planned.label || 'Время'} onDone={completeSet} haptics={settings?.haptics} />
        ) : (
          <div className="reps-and-effort">
            <div className="reps-card">
              <span>План: {sideLabel ? `${formatRepetitions(planned.sets[flow.setIndex])} · ${sideLabel.toLowerCase()}` : planned.label || formatRepetitions(planned.sets[flow.setIndex])}</span>
              <NumberWheel value={actual[planned.exerciseId]?.[flow.setIndex] ?? planned.sets[flow.setIndex]} max={Math.max(30, planned.sets[flow.setIndex] + 15)} onChange={updateReps} />
            </div>
            {exercise.category === 'strength' && <div className="difficulty">
              <span>Повторов осталось в запасе</span>
              <div className="segmented segmented--rir" role="group" aria-label="Повторы в запасе">
                {([0,1,2,3,4] as RepsInReserve[]).map((value) => <button key={value} className={rir === value ? 'is-selected' : ''} onClick={() => setRir(value)} aria-label={`${value === 4 ? 'Четыре или больше' : value} повторов в запасе`}>{value === 4 ? '4+' : value}</button>)}
              </div>
              <small>0 — предел · 2 — хороший запас · 4+ — очень легко</small>
            </div>}
          </div>
        )}

        {(exercise.breathing || exercise.regression || exercise.stopSignal) && <div className="cue-grid">
          {exercise.breathing && <span><small>Дыхание</small><strong>{exercise.breathing}</strong></span>}
          {exercise.regression && <span><small>Если тяжело</small><strong>{exercise.regression}</strong></span>}
          {exercise.stopSignal && <span className="cue-grid__stop"><small>Стоп</small><strong>{exercise.stopSignal}</strong></span>}
        </div>}

        <details className="technique-card guide-card">
          <summary>Схема и порядок движения <span>+</span></summary>
          <ExerciseGuide exercise={exercise} />
        </details>
        <details className="technique-card">
          <summary>Техника и частые ошибки <span>+</span></summary>
          <div className="technique-columns"><div><b>Делай</b><ul>{exercise.technique.map((item) => <li key={item}>{item}</li>)}</ul></div><div><b>Не делай</b><ul>{exercise.mistakes.map((item) => <li key={item}>{item}</li>)}</ul></div></div>
        </details>

        {showPainChoice && <div className="notice notice--warning pain-choice" role="alert"><strong>Остановить это упражнение?</strong><span>Это не считается провалом тренировки.</span><div><button className="button button--secondary" onClick={skipExercise}>Да, перейти дальше</button><button className="button button--ghost" onClick={() => setShowPainChoice(false)}>Продолжить осторожно</button></div></div>}
        <div className="quiet-actions">
          {exercise.kneeSensitive && <button onClick={reportKneePain}>Колено стало болеть</button>}
          {planned.optional && <button onClick={skipExercise}>Пропустить по желанию</button>}
        </div>

        {planned.unit === 'reps' && <div className="workout-commit-bar"><button className="button button--primary button--wide" onClick={completeSet}>Записать · {formatRepetitions(actual[planned.exerciseId]?.[flow.setIndex] ?? planned.sets[flow.setIndex])}</button></div>}
      </section>}

      {flow.phase === 'rest' && planned && <section key={`rest-${flow.transitionId}`} className={`workout-panel workout-panel--center rest-panel${motionClass}`}><Timer seconds={flow.restSeconds || 45} title={session.format === 'circuit' && planned.section === 'work' ? `Отдых · дальше круг ${flow.setIndex + 1}` : `Отдых · дальше подход ${flow.setIndex + 1}`} onDone={() => dispatch({ type: 'REST_DONE' })} haptics={settings?.haptics} /></section>}

      {flow.phase === 'finish' && <section key={`finish-${flow.transitionId}`} className={`workout-panel${motionClass}`}>
        <p className="eyebrow">Почти готово</p>
        <h1>Как всё прошло?</h1>
        <RangeInput label="Энергия после" value={energyAfter} onChange={setEnergyAfter} leftLabel="нет сил" rightLabel="много сил" />
        {needsCheckin && <RangeInput label="Правое колено после" value={kneeAfter} onChange={setKneeAfter} leftLabel="ничего" rightLabel="сильная боль" />}
        <label className="field"><span>Общая сложность</span><select value={overallDifficulty} onChange={(event) => setOverallDifficulty(Number(event.target.value) as Difficulty)}><option value="1">1 · Очень легко</option><option value="2">2 · Легко</option><option value="3">3 · Нормально</option><option value="4">4 · Тяжело</option><option value="5">5 · Предел</option></select></label>
        <label className="field"><span>Заметка <small>необязательно</small></span><textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Например: плохо спал, подтягивания тяжёлые, колено нормально" /></label>
        {error && <div className="notice notice--error" role="alert">{error}</div>}
        <button className="button button--primary button--wide" disabled={saving} onClick={finish}>{saving ? 'Сохраняю…' : 'Сохранить тренировку'}</button>
      </section>}

      {flow.phase === 'saved' && <section key={`saved-${flow.transitionId}`} className={`workout-panel workout-panel--center completion${motionClass}`}>
        <div className="completion__mark" aria-hidden="true">✓</div>
        <p className="eyebrow">Сохранено локально</p>
        <h1>На сегодня всё</h1>
        <p>{program.principle}</p>
        <button className="button button--primary button--wide" onClick={onClose}>Готово</button>
      </section>}

      {undoState && flow.phase !== 'saved' && <div className="undo-toast" role="status"><span><strong>Подход записан</strong><small>{undoState.message}</small></span><button onClick={undo}>Отменить</button></div>}
    </div>
  )
}
